import { cueIdValido } from '@sol-pool/engine-physics'
import { atendePedido, type PedidoDeTaco, type TacoEmJogo } from '@sol-pool/tacos'

/**
 * Qual taco este jogador escolheu, e se o seletor sequer aparece.
 *
 * ESCONDIDO DE PROPÓSITO, e o esconderijo NÃO é a segurança. Quem autoriza o
 * taco é o servidor (`apps/server/src/tacos.ts`): mesmo que alguém ache esta
 * chave e peça o melhor taco, o servidor devolve o padrão enquanto
 * `TACOS_LIBERADOS` estiver desligada lá. O taco entra na física e vai gravado
 * no replay — uma escolha que o cliente fizesse sozinho seria escolher os
 * próprios atributos, que é a classe de furo do efeito além do limite de falha.
 *
 * Então o que esta flag faz é MOSTRAR o seletor, não conceder taco nenhum. Ela
 * existe para experimentar o equilíbrio antes de haver NFT, sem que o seletor
 * apareça para quem entra numa mesa valendo dinheiro.
 *
 * Módulo puro, como `game/controle.ts`, e pelo mesmo motivo: ler
 * `localStorage` falha de três jeitos — bloqueado, valor antigo, valor
 * inventado — e nenhum deles pode derrubar a mesa.
 */

/** Onde a escolha fica guardada entre partidas. */
export const CHAVE_DO_TACO = 'zinc.tacos.escolhido'

/** O mínimo de `Storage` que se usa: dá para passar um objeto qualquer no teste. */
export type Armazem = Pick<Storage, 'getItem' | 'setItem'>

/** O pedido de quem nunca escolheu. */
const PEDIDO_PADRAO: PedidoDeTaco = { id: 'padrao' }

/**
 * O pedido guardado: `{ id }` de um gratuito ou `{ mint }` de um NFT, em JSON.
 *
 * Valor antigo (o id solto, de antes dos NFTs) e qualquer lixo caem no padrão:
 * o servidor conferiria de qualquer forma, mas aqui é onde a tela decide o que
 * marcar — e um pedido que ninguém atende deixaria o seletor sem nada aceso.
 *
 * O mint não é validado além do comprimento: quem sabe se ele existe é a
 * chain, e quem confere a posse é o servidor. O que sai daqui é um PEDIDO.
 */
export function lerPedido(armazem: Armazem | null): PedidoDeTaco {
  try {
    const bruto = armazem?.getItem(CHAVE_DO_TACO)
    if (!bruto) return PEDIDO_PADRAO
    const v: unknown = JSON.parse(bruto)
    if (!v || typeof v !== 'object') return PEDIDO_PADRAO
    if ('mint' in v && typeof v.mint === 'string' && v.mint.length >= 32) return { mint: v.mint }
    if ('id' in v && cueIdValido(v.id)) return { id: v.id }
    return PEDIDO_PADRAO
  } catch {
    return PEDIDO_PADRAO
  }
}

/** Guarda o pedido. Falhar em silêncio é o certo: é preferência, não partida. */
export function guardarPedido(armazem: Armazem | null, pedido: PedidoDeTaco): void {
  try {
    armazem?.setItem(CHAVE_DO_TACO, JSON.stringify(pedido))
  } catch {
    // sem armazenamento, sem memória — e sem erro na tela
  }
}

/**
 * Qual, entre os tacos que o servidor mandou, é o pedido.
 *
 * `undefined` = o pedido envelheceu: o NFT saiu da carteira, ou a lista ainda
 * não chegou. Quem chama decide o que fazer — o seletor não acende nada.
 */
export const tacoEscolhido = (tacos: readonly TacoEmJogo[], pedido: PedidoDeTaco): TacoEmJogo | undefined =>
  tacos.find((t) => atendePedido(t, pedido))
